import React, { useState, useMemo, useEffect } from "react";
import { useTable, Column } from "react-table";
import { copy } from "shared/assets";
import { Question } from "shared/assets/icons";

import { Action, ActionDropdown, Checkbox, Tooltip } from "shared/components";
import { OpenRow } from "./components/OpenRow";
import { Icon, ItalicText, MutedText } from "shared/styles";
import { colors } from "shared/styles/constants";

import { TableProps } from "./interfaces";

import {
  StyledTable,
  StyledThead,
  StyledHeaderRow,
  StyledCell,
  StyledRow,
  StyledTooltip,
  TableOverflowWrapper,
  TableScrollWrapper,
  EmptyTableContent,
} from "./styles";
import { useSetAtom } from "jotai";
import { visibleRowsAtom } from "./table-atoms";

export type TableColumn = Column & {
  hideOnMobile?: boolean;
  description?: string;
  columns?: Column[];
};

export const Table: React.FC<TableProps> = ({
  columns,
  emptyCopy,
  theme,
  data = [],
  getRowActions,
  openRow,
  closeOpenRow = () => {},
  dataTestId,
  options,
  noScroll = false,
  options: {
    isStriped = true,
    showActions = true,
    verticalAlignCells = "middle",
    showCheckboxes = false,
    handleCheckboxChange,
    selectedEvents,
  } = {},
}) => {
  const setVisibleRows = useSetAtom(visibleRowsAtom);
  const [referenceElement, setReferenceElement] = useState<Element | null>();
  const [tooltipContent, setTooltipContent] = useState<string | "">();

  const computedColumns: Column[] = useMemo(() => {
    if (!getRowActions || !showActions) {
      return columns;
    }

    return [
      ...columns,
      {
        Header: copy.actions.actions,
        accessor: "actions",
        Cell: ({ row }) => {
          const rowId: string = (row.original as any)?.id;

          if (showCheckboxes) {
            return (
              <Checkbox
                name="select"
                value={rowId}
                checked={selectedEvents?.includes(rowId)}
                onChange={e => handleCheckboxChange?.(e.target.value)}
              />
            );
          }

          // if there's no actions, don't render dropdown
          if (getRowActions(rowId).length === 0) {
            return null;
          }

          // if there's only one actions, don't render dropdown
          // render an action button
          if (getRowActions(rowId).length === 1) {
            const singleAction = getRowActions(rowId)[0];

            return <Action {...singleAction} handleClick={() => singleAction.handleClick()} />;
          }

          // else render dropdown
          return (
            <ActionDropdown
              options={getRowActions(rowId)}
              label={copy.actions.actions}
              placement="bottom-end"
              dataTestId="table_actions"
              wrapOptions
            />
          );
        },
      },
    ];
  }, [getRowActions, showActions, columns, handleCheckboxChange, selectedEvents, showCheckboxes]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns: computedColumns,
    data,
  });

  useEffect(() => {
    try {
      if (!rows?.length) {
        setVisibleRows([]);
        return;
      }

      const visibleRowIds = rows.map(row => (row.original as any)?.id);

      setVisibleRows(visibleRowIds);
    } catch {}
  }, [rows, setVisibleRows]);

  return (
    <TableOverflowWrapper data-testid={dataTestId}>
      <TableScrollWrapper noScroll={noScroll}>
        <StyledTable {...getTableProps()} isOutline={options?.isOutline} width={options?.width}>
          {!options?.hideHeader && (
            <StyledThead>
              {headerGroups.map(headerGroup => (
                <StyledHeaderRow
                  {...headerGroup.getHeaderGroupProps()}
                  theme={theme}
                  isMultiHeading={options?.isMultiHeading}
                >
                  {headerGroup.headers.map((column, index) => {
                    const { description } = column as TableColumn;

                    return (
                      <StyledCell
                        {...column.getHeaderProps()}
                        hideOnMobile={columns[index]?.hideOnMobile}
                        isActionColumn={column.id === "actions"}
                        isOutline={options?.isOutline}
                        verticalAlignCells={verticalAlignCells}
                        theme={theme}
                        style={{
                          maxWidth: column.maxWidth ?? "unset",
                          minWidth: column.minWidth ?? "unset",
                        }}
                        key={index}
                      >
                        {column.render("Header")}
                        {description && (
                          <StyledTooltip>
                            <Icon
                              padding="3px"
                              isCircle
                              size="l"
                              cursor="pointer"
                              as={Question}
                              color={colors.white}
                              backgroundColor={colors.grey1}
                              onMouseOver={(event: any) => {
                                setReferenceElement(event.currentTarget);
                                setTooltipContent(description);
                              }}
                            />
                          </StyledTooltip>
                        )}
                      </StyledCell>
                    );
                  })}
                </StyledHeaderRow>
              ))}
            </StyledThead>
          )}

          {!!rows.length && (
            <tbody {...getTableBodyProps()}>
              {rows.map((row, i) => {
                prepareRow(row);
                const isOpen = openRow && (row.original as any)?.id === openRow?.id;
                const stripedRow = isStriped ? i % 2 === 0 : false;

                return (
                  <React.Fragment key={i}>
                    <StyledRow
                      {...row.getRowProps()}
                      expanded={isOpen}
                      stripedRow={stripedRow}
                      isOutline={options?.isOutline}
                      theme={theme}
                    >
                      {row.cells.map((cell, index) => {
                        return (
                          <StyledCell
                            {...cell.getCellProps()}
                            hideOnMobile={columns[index]?.hideOnMobile}
                            isActionColumn={cell.column.id === "actions"}
                            stripedRow={stripedRow}
                            isOutline={options?.isOutline}
                            verticalAlignCells={verticalAlignCells}
                            theme={theme}
                            style={{
                              maxWidth: cell.column.maxWidth ?? "unset",
                              minWidth: cell.column.minWidth ?? "unset",
                            }}
                          >
                            {cell.render("Cell")}
                          </StyledCell>
                        );
                      })}
                    </StyledRow>
                    {isOpen && (
                      <OpenRow
                        colSpan={computedColumns.length}
                        stripedRow={stripedRow}
                        isOutline={options?.isOutline}
                        theme={theme}
                        handleClose={closeOpenRow}
                      >
                        {openRow?.content}
                      </OpenRow>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          )}
        </StyledTable>
        {!rows.length && emptyCopy && (
          <EmptyTableContent>
            <MutedText>
              <ItalicText>{emptyCopy}</ItalicText>
            </MutedText>
          </EmptyTableContent>
        )}
      </TableScrollWrapper>
      <Tooltip
        content={tooltipContent}
        referenceElement={referenceElement}
        placement="top"
        maxWidth={300}
      />
    </TableOverflowWrapper>
  );
};
